import { useEffect, useRef, useState, useCallback } from "react";
import { Upload, FlipHorizontal, RotateCcw, Download, ImagePlus } from "lucide-react";
import { useSaveImage, isMobile } from "@/components/SaveImage";
import { FRAMES, getFrame, drawFrame, CANVAS_SIZE, logosReady, SWATCH } from "@/lib/frames";

export default function FrameEditor({
  canvasRef,
  image,
  rawDataUrl,
  onPhoto,
  frameId,
  setFrameId,
  transform,
  setTransform,
  name,
  setName,
  showName,
  setShowName,
  onDownload,
  hiddenFrames,
}) {
  const framesReady = Array.isArray(hiddenFrames);
  hiddenFrames = hiddenFrames || [];
  const [fontsReady, setFontsReady] = useState(false);
  const [logosOk, setLogosOk] = useState(false);
  const dragging = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!("fonts" in document)) {
      setFontsReady(true);
      return;
    }
    Promise.all([document.fonts.load("800 80px Jost"), document.fonts.load("700 28px Jost")])
      .then(() => document.fonts.ready)
      .then(() => setFontsReady(true))
      .catch(() => setFontsReady(true));
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    drawFrame(ctx, image, transform, getFrame(frameId), { name, showName });
  }, [canvasRef, image, transform, frameId, name, showName]);

  useEffect(() => {
    logosReady.then(() => setLogosOk(true));
  }, []);

  useEffect(() => {
    redraw();
  }, [redraw, fontsReady, logosOk]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        onPhoto(dataUrl, img);
        setTransform({ scale: 1, rotate: 0, offsetX: 0, offsetY: 0, flipH: false });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const onPointerDown = (e) => {
    if (!image) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const f = CANVAS_SIZE / rect.width;
    const dx = (e.clientX - dragging.current.x) * f;
    const dy = (e.clientY - dragging.current.y) * f;
    dragging.current = { x: e.clientX, y: e.clientY };
    setTransform((t) => ({ ...t, offsetX: t.offsetX + dx, offsetY: t.offsetY + dy }));
  };
  const onPointerUp = () => {
    dragging.current = null;
  };

  const { save: saveImg, sheet: saveSheet } = useSaveImage();
  const download = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      saveImg(blob, "blackgirljoy-winner-frame.png", "My #BlackGirlJoyChallenge winner frame");
    }, "image/png");
    if (onDownload) onDownload();
  };

  const sbgFrames = FRAMES.filter((f) => f.group === "sbg" && !hiddenFrames.includes(f.id));
  const ptFrames = FRAMES.filter((f) => f.group === "pt" && !hiddenFrames.includes(f.id));

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
      {/* canvas / upload */}
      <div>
        <div className="relative overflow-hidden rounded-3xl bg-white p-3 shadow-[0_18px_50px_rgba(173,28,93,0.18)] ring-1 ring-sbg-raspberry/10">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            data-testid="frame-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className="block w-full rounded-2xl"
            style={{ touchAction: "none", cursor: image ? "grab" : "default" }}
          />
          {!image && (
            <button
              type="button"
              data-testid="upload-photo-btn"
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-3 flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-sbg-raspberry/40 bg-sbg-blush/30 text-center transition-colors hover:bg-sbg-blush/60"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-sbg-raspberry text-white">
                <ImagePlus className="h-8 w-8" />
              </span>
              <span className="text-lg font-bold text-sbg-burgundy">Add your photo</span>
              <span className="max-w-xs text-sm text-sbg-burgundy/70">
                Click to choose a photo. A clear photo of your face works best. Drag it to reposition.
              </span>
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          data-testid="photo-file-input"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {image && (
          <p className="mt-3 text-center text-xs font-medium text-sbg-burgundy/60">
            🔒 Drag the photo to move it. Your original photo is stored privately for Southern Black Girls only.
          </p>
        )}
      </div>

      {/* controls */}
      <div className="space-y-6">
        {!framesReady && (
          <div data-testid="frames-loading" className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-sbg-blush/50" />)}
          </div>
        )}
        {framesReady && sbgFrames.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-sbg-raspberry">
            Southern Black Girls frames
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {sbgFrames.map((f) => (
              <FrameSwatch key={f.id} frame={f} active={frameId === f.id} onClick={() => setFrameId(f.id)} />
            ))}
          </div>
        </div>
        )}
        {framesReady && ptFrames.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-pt-purple">
            Pete &amp; Thomas partner frames
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {ptFrames.map((f) => (
              <FrameSwatch key={f.id} frame={f} active={frameId === f.id} onClick={() => setFrameId(f.id)} />
            ))}
          </div>
        </div>
        )}

        {/* name */}
        <div className="rounded-2xl bg-white p-4 ring-1 ring-sbg-raspberry/10">
          <label className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-sbg-burgundy">Show my name on the frame</span>
            <input
              type="checkbox"
              data-testid="show-name-toggle"
              checked={showName}
              onChange={(e) => setShowName(e.target.checked)}
              className="h-5 w-5 accent-sbg-fuchsia"
            />
          </label>
          <input
            type="text"
            data-testid="frame-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="mt-3 w-full rounded-xl border border-sbg-raspberry/20 bg-background px-3 py-2 text-sm outline-none focus:border-sbg-fuchsia"
          />
        </div>

        {/* adjust */}
        {image && (
          <div className="space-y-4 rounded-2xl bg-white p-4 ring-1 ring-sbg-raspberry/10">
            <div>
              <div className="mb-1 flex justify-between text-xs font-semibold text-sbg-burgundy">
                <span>Zoom</span>
                <span>{Math.round(transform.scale * 100)}%</span>
              </div>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                data-testid="zoom-slider"
                value={transform.scale}
                onChange={(e) => setTransform((t) => ({ ...t, scale: parseFloat(e.target.value) }))}
                className="joy-range w-full"
              />
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs font-semibold text-sbg-burgundy">
                <span>Rotate</span>
                <span>{transform.rotate}°</span>
              </div>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                data-testid="rotate-slider"
                value={transform.rotate}
                onChange={(e) => setTransform((t) => ({ ...t, rotate: parseInt(e.target.value, 10) }))}
                className="joy-range w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="flip-btn"
                onClick={() => setTransform((t) => ({ ...t, flipH: !t.flipH }))}
                className="flex items-center gap-1.5 rounded-full bg-sbg-blush px-3 py-2 text-xs font-bold text-sbg-raspberry transition-transform hover:-translate-y-0.5"
              >
                <FlipHorizontal className="h-4 w-4" /> Flip
              </button>
              <button
                data-testid="reset-btn"
                onClick={() => setTransform({ scale: 1, rotate: 0, offsetX: 0, offsetY: 0, flipH: false })}
                className="flex items-center gap-1.5 rounded-full bg-sbg-blush px-3 py-2 text-xs font-bold text-sbg-raspberry transition-transform hover:-translate-y-0.5"
              >
                <RotateCcw className="h-4 w-4" /> Reset
              </button>
              <button
                data-testid="change-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 rounded-full bg-sbg-blush px-3 py-2 text-xs font-bold text-sbg-raspberry transition-transform hover:-translate-y-0.5"
              >
                <Upload className="h-4 w-4" /> Change
              </button>
            </div>
            <button
              data-testid="download-frame-btn"
              onClick={download}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-sbg-fuchsia px-4 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
            >
              <Download className="h-4 w-4" /> {isMobile() ? "Save my frame to Photos" : "Download my frame"}
            </button>
          </div>
        )}
      </div>
      {saveSheet}
    </div>
  );
}

function FrameSwatch({ frame, active, onClick }) {
  const sw = SWATCH[frame.id] || { bg: "#3A0300", accent: "#E02189" };
  return (
    <button
      type="button"
      data-testid={`frame-option-${frame.id}`}
      onClick={onClick}
      className={`overflow-hidden rounded-xl bg-white text-left transition-all hover:-translate-y-0.5 ${
        active ? "ring-2 ring-sbg-fuchsia ring-offset-2" : "ring-1 ring-sbg-raspberry/15"
      }`}
    >
      <span className="flex h-12 w-full items-center justify-center" style={{ background: sw.bg }}>
        <span className="h-7 w-7 rounded-full" style={{ border: `3px solid ${sw.accent}` }} />
      </span>
      <span className="block px-2 py-1.5 text-[11px] font-bold text-sbg-burgundy">{frame.label}</span>
    </button>
  );
}
